import IconSubscript from "@shared/components/icons/Subscript"
import IconSuperscript from "@shared/components/icons/Superscript"
import IconTextBold from "@shared/components/icons/TextBold"
import IconTextItalic from "@shared/components/icons/TextItalic"
import IconTextStrikethrough from "@shared/components/icons/TextStrikethrough"
import IconTextUnderline from "@shared/components/icons/TextUnderline"
import { Dropdown } from "@shared/components/untitledui/base/dropdown/dropdown"
import {
  OPERATION_CONTRACTS,
  type OperationContract,
} from "@shared/unified-tiptap/analytics/operations"
import { modKeyText } from "@shared/unified-tiptap/util/is-apple"
import type { Editor } from "@tiptap/core"
import { useEditorState } from "@tiptap/react"
import { useCallback, type ReactNode } from "react"
import { ToolbarToggleButton } from "./ToolbarToggleButton"

export type MarkName = "bold" | "italic" | "underline" | "strike" | "superscript" | "subscript"

interface MarkInfo {
  icon: ReactNode
  displayName: string
  shortcut: string
  operation: OperationContract<null>
}

const markInfo: Record<MarkName, MarkInfo> = {
  bold: {
    icon: <IconTextBold />,
    displayName: "Bold",
    shortcut: modKeyText + "+B",
    operation: OPERATION_CONTRACTS.OP_TOGGLE_BOLD,
  },
  italic: {
    icon: <IconTextItalic />,
    displayName: "Italic",
    shortcut: modKeyText + "+I",
    operation: OPERATION_CONTRACTS.OP_TOGGLE_ITALIC,
  },
  underline: {
    icon: <IconTextUnderline />,
    displayName: "Underline",
    shortcut: modKeyText + "+U",
    operation: OPERATION_CONTRACTS.OP_TOGGLE_UNDERLINE,
  },
  strike: {
    icon: <IconTextStrikethrough />,
    displayName: "Strikethrough",
    shortcut: modKeyText + "+Shift+S",
    operation: OPERATION_CONTRACTS.OP_TOGGLE_STRIKETHROUGH,
  },
  superscript: {
    icon: <IconSuperscript />,
    displayName: "Superscript",
    shortcut: modKeyText + "+,",
    operation: OPERATION_CONTRACTS.OP_TOGGLE_SUPERSCRIPT,
  },
  subscript: {
    icon: <IconSubscript />,
    displayName: "Subscript",
    shortcut: modKeyText + "+.",
    operation: OPERATION_CONTRACTS.OP_TOGGLE_SUBSCRIPT,
  },
}

function useMarkState(editor: Editor, type: MarkName) {
  const isActive = useEditorState({
    editor,
    selector: (snapshot) => snapshot.editor.isActive(type),
  })
  const canToggle = useEditorState({
    editor,
    selector: (snapshot) => snapshot.editor.can().toggleMark(type),
  })

  const handlePress = useCallback(
    () =>
      editor
        .chain()
        .label(markInfo[type].operation, "floating-toolbar", null)
        .toggleMark(type)
        .run(),
    [editor, type],
  )

  return { isActive, canToggle, handlePress }
}

export function MarkButton({ editor, type }: { editor: Editor; type: MarkName }) {
  const info = markInfo[type]
  const { isActive, canToggle, handlePress } = useMarkState(editor, type)

  return (
    <ToolbarToggleButton
      isSelected={isActive}
      onToggle={handlePress}
      isDisabled={!canToggle}
      ariaLabel={info.displayName}
      tooltip={
        <>
          {info.displayName}
          <span className="ml-2 text-gray-400">{info.shortcut}</span>
        </>
      }
    >
      {info.icon}
    </ToolbarToggleButton>
  )
}

export function MarkMenuItem({ editor, type }: { editor: Editor; type: MarkName }) {
  const info = markInfo[type]
  const { isActive, canToggle, handlePress } = useMarkState(editor, type)

  return (
    <Dropdown.Item
      textValue={info.displayName}
      isDisabled={!canToggle}
      isActive={isActive}
      aria-label={info.displayName}
      onAction={handlePress}
    >
      <span className="flex w-full items-center justify-between gap-4">
        <span className="flex items-center gap-2">
          {info.icon} {info.displayName}
        </span>
        <span className="text-gray-400">{info.shortcut}</span>
      </span>
    </Dropdown.Item>
  )
}
